import { ApiError } from '@/api/client';
import { useProjectWord } from '@/api/projects';
import { type Cap, capsFrom, useCan } from '@/auth/caps';
import { logout, useAuth } from '@/auth/store';
import { T, useI18n } from '@/i18n';
import { identify, measureDemo, sessionStarted, stopRecording } from '@/marketing/analytics';
import { appPageOf, countAppPage } from '@/marketing/count';
import { PocChatPanel } from '@/poc-chat/Panel';
import { Bell } from '@/shell/Bell';
import { DemoBanner, SetupBanner, TrialBanner, TrialChip } from '@/shell/DemoBanner';
import { HelpButton, helpUrl } from '@/shell/HelpButton';
import { GlobalSearch, SearchButton } from '@/shell/Search';
import { Welcome } from '@/shell/Welcome';
import { announce, setDestinationCheck, setPhase, useContextStore } from '@/shell/context';
import { LeaveGuard } from '@/shell/leave';
import { useCompanySwitch } from '@/shell/switch';
import { usePolicyWatch, useTenant } from '@/shell/tenant';
import { type Theme, applyTheme, readTheme } from '@/shell/theme';
import { useNewVersion } from '@/shell/version';
import { SupportBanner } from '@/support/Support';
import { Button, Menu, Toast } from '@/ui';
import { avTone, properName } from '@/ui/format';
import { HuginMark } from '@/ui/icons';
import { Icon, type IconName } from '@/ui/icons';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

/**
 * A destination belongs to an optional workflow (`module`) or to core Time. It
 * is active when the server says the workflow is enabled AND available for
 * this company and the person holds the capability (module-role acceptance,
 * 2026-09-14). Order and styles are the ones next ships.
 */
/* The menu icons come from the one shared set (ui/icons); each is decorative, the label names it. */
const Ic = ({ name }: { name: IconName }) => (
  <span className="ic">
    <Icon name={name} />
  </span>
);

interface Item {
  to: string;
  icon: IconName;
  label: string;
  module?: 'scheduling' | 'projects';
  cap?: Cap;
  /** admin: the manager's jobs; settings: below the divider; self: the person's own days. */
  group: 'admin' | 'settings' | 'self';
  /** shown only to someone without this capability */
  unless?: Cap;
  /** the NavLink matches this path only, not the pages under it */
  end?: boolean;
  /** the label when the admin group shows too, where the plain one would appear twice */
  besideAdmin?: string;
  /** in the sidebar only when there is no admin group; a manager finds it in the menu under the name */
  alone?: boolean;
  /** other paths that belong to this destination and light it (an old page now one of its tabs) */
  also?: string[];
}
/**
 * The manager's jobs (manager redesign, 2026-10-09): Oversikt holds Dagen and Oppfølging, Kontroll
 * (was Timer) holds the review and the records, Rapporter (was Timer til lønn) holds Lønnsprosent.
 * The menu reads plan, review, report (owner, 2026-10-19). The old pages keep their addresses
 * and light the destination they now belong to.
 */
const ITEMS: Item[] = [
  {
    to: '/hjem',
    icon: 'home',
    label: 'Oversikt',
    cap: 'timeliste.se',
    group: 'admin',
    also: ['/dagen', '/perioden', '/varsler'],
  },
  // a planner without the timesheet still has the day's list
  {
    to: '/dagen',
    icon: 'home',
    label: 'Oversikt',
    module: 'scheduling',
    cap: 'vaktplan.se',
    unless: 'timeliste.se',
    group: 'admin',
  },
  // one week grid for every company (#118, plan 0033 decision 2): «Vaktplan» with planning on,
  // «Uka» with it off, where the cards are what the clock-ins made (labelOf)
  {
    to: '/vaktplan',
    icon: 'calendar',
    label: 'Vaktplan',
    cap: 'vaktplan.se',
    group: 'admin',
  },
  {
    to: '/timeliste',
    icon: 'clock',
    label: 'Kontroll',
    cap: 'timeliste.se',
    group: 'admin',
    also: ['/arbeidstid'],
  },
  {
    to: '/lonn',
    icon: 'banknote',
    label: 'Rapporter',
    cap: 'timerloenn.se',
    group: 'admin',
    // the Personalliste and the §10-7 print live on /arbeidstid but open from here (#173)
    also: ['/omsetning', '/deltid', '/arbeidstid?vis=personalliste', '/arbeidstid?vis=utskrift'],
  },
  {
    to: '/fravaer',
    icon: 'calendarOff',
    label: 'Fravær',
    cap: 'fravaer.se',
    group: 'admin',
  },
  {
    to: '/ansatte',
    icon: 'users',
    label: 'Ansatte',
    cap: 'ansatte.endre',
    group: 'admin',
  },
  {
    to: '/prosjekter',
    icon: 'folder',
    label: 'Prosjekter',
    module: 'projects',
    cap: 'prosjekter.se',
    group: 'admin',
  },
  // the work cycle's steps from saved facts (#294); always there, also after everything is done (ONB-03)
  {
    to: '/kom-i-gang',
    icon: 'flag',
    label: 'Kom i gang',
    cap: 'ansatte.endre',
    group: 'settings',
  },
  {
    to: '/innstillinger/arbeidsmate',
    icon: 'sliders',
    label: 'Innstillinger',
    cap: 'innstillinger.se',
    group: 'settings',
    also: ['/innstillinger'],
  },
  // the person's own days, below the company's (owner, 2026-09-21: user and admin apart). An
  // employee sees only these five (owner's employee UI brief, 2026-09-22); a manager keeps them
  // under Personlig. Laget is the colleague list too, so it needs no workflow: with the rota off
  // or colleagues' shifts hidden it still names people and how to reach them.
  {
    to: '/min',
    icon: 'home',
    label: 'Min side',
    cap: 'egen.se',
    group: 'self',
    end: true,
  },
  {
    to: '/min/plan',
    icon: 'calendar',
    label: 'Min plan',
    cap: 'egen.se',
    group: 'self',
  },
  {
    to: '/min/laget',
    icon: 'users',
    label: 'Laget',
    cap: 'ansatte.se',
    group: 'self',
  },
  // beside the manager's own Fravær the personal one needs its own name
  {
    to: '/min/fravaer',
    icon: 'calendarOff',
    label: 'Fravær',
    besideAdmin: 'Mitt fravær',
    cap: 'egen.se',
    group: 'self',
  },
  {
    to: '/min/profil',
    icon: 'user',
    label: 'Profil',
    cap: 'egen.se',
    group: 'self',
    alone: true,
  },
];
const GROUPS = [
  { key: 'admin', label: 'Administrasjon' },
  { key: 'settings', label: 'Innstillinger' },
  { key: 'self', label: 'Personlig' },
] as const;

/** an address in `also`: a path and the pages under it, or a path whose query holds these values */
const at = (p: string, pathname: string, search: string) => {
  const [path, q] = p.split('?');
  if (!q) return pathname === p || pathname.startsWith(`${p}/`);
  const has = new URLSearchParams(search);
  return pathname === path && [...new URLSearchParams(q)].every(([k, v]) => has.get(k) === v);
};
/** the destination a path belongs to: its own address, the pages under it, or an old page it took in;
 * an address with a query wins over the bare path, so /arbeidstid?vis=utskrift is Rapporter's.
 * ponytail: someone without Rapporter sees nothing lit on those two views; check visibility if it matters */
const lit = (it: Item, pathname: string, search = '') => {
  const q = ITEMS.find((x) => x.also?.some((p) => p.includes('?') && at(p, pathname, search)));
  if (q) return it === q;
  return (
    pathname === it.to ||
    (!it.end && pathname.startsWith(`${it.to}/`)) ||
    !!it.also?.some((p) => at(p, pathname, search))
  );
};

export default function Shell() {
  const can = useCan();
  const locale = useI18n((s) => s.locale);
  // a tab open across a deploy is told to reload (shell/version.ts)
  const newVersion = useNewVersion();
  // Light, dark, or the machine's choice, remembered across reloads. See shell/theme.ts for why
  // this exists: the media query alone could not be asked for dark on a light machine.
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  const pickTheme = (t: Theme) => {
    applyTheme(t);
    setTheme(t);
  };
  const THEMES: [Theme, string][] = [
    ['system', 'Tema: følg maskinen'],
    ['light', 'Tema: lyst'],
    ['dark', 'Tema: mørkt'],
  ];
  const themeItems = THEMES.map(([t, label]) => ({
    key: `theme-${t}`,
    label: `${theme === t ? '✓ ' : ''}${T(label)}`,
    onSelect: () => pickTheme(t),
  }));
  const user = useAuth((s) => s.user);
  const clear = useAuth((s) => s.clear);
  const nav = useNavigate();
  const { pathname, search } = useLocation();
  // Personlig folds up beside the admin group (owner, 2026-09-27), remembered per browser, and is
  // open whenever one of its pages is the one showing
  const [selfOpen, setSelfOpen] = useState(() => {
    try {
      return localStorage.getItem('timefly.side.self') === 'open';
    } catch {
      return false;
    }
  });
  const [sideCollapsed, setSideCollapsed] = useState(() => {
    try {
      return localStorage.getItem('timefly.side.collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const toggleSidebar = () =>
    setSideCollapsed((previous) => {
      try {
        localStorage.setItem('timefly.side.collapsed', String(!previous));
      } catch {}
      return !previous;
    });
  const toggleSelf = () =>
    setSelfOpen((o) => {
      try {
        localStorage.setItem('timefly.side.self', o ? 'closed' : 'open');
      } catch {}
      return !o;
    });
  const { tenant, tenants, me, mode, workspace, tenantStatus, tenantError, refetchTenant } = useTenant();
  const projectWord = useProjectWord();
  // product analytics (issue #15): who and which company, and one app_session_started per 30 minutes;
  // nothing unless the visitor said yes to statistics on timefly.no
  const userId = useAuth((s) => s.user?.id);
  // staff looking as somebody (#23) is not that person's visit: no identify, no session, no replay
  const supporting = useAuth((s) => !!s.user?.support);
  // a replay started on a public sign-up page ends here (analytics.ts), before the first paint, so no
  // frame of the app is recorded; a demo company starts its own once it is known (measureDemo)
  useLayoutEffect(() => stopRecording(), []);
  // before the paint, like the stop above: a switch from a demo to a real company must not let one
  // frame of the real one into the demo's recording
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per person and company, not on every re-read of the company
  useLayoutEffect(() => {
    if (!userId || !tenant || supporting) return;
    identify(userId, tenant);
    sessionStarted(tenant.me?.role ?? null, tenant);
  }, [userId, tenant?.id, supporting]);
  // Replays and heatmaps in a demo company only (#43, owner 2026-10-07), fail-closed: on only while a
  // demo company is confirmed loaded and no switch is under way; off while loading, switching or after
  // an error, and before the paint (security review 2026-10-07: it stayed on until the next company loaded)
  const switching = useContextStore((s) => s.phase.kind !== 'idle');
  const demoNow = tenantStatus === 'success' && tenant?.is_demo === true && !switching;
  useLayoutEffect(() => {
    measureDemo(!!userId && demoNow && !supporting);
  }, [userId, demoNow, supporting]);
  // which pages are opened, without cookies (owner 2026-10-13): one count per page per visit, once the
  // company is known so the kind is right; a route inside the same page (a tab, an employee) counts nothing
  const lastPage = useRef<string | null>(null);
  useEffect(() => {
    const page = appPageOf(pathname);
    if (!page || !tenant || supporting || page === lastPage.current) return;
    lastPage.current = page;
    countAppPage(page, tenant.is_demo ? 'demo' : 'bedrift');
  }, [pathname, tenant, supporting]);
  // a permission taken away upstairs clears what was fetched under the old one
  usePolicyWatch();
  const { switchTo, stay, discardAndSwitch, dismiss } = useCompanySwitch();
  const phase = useContextStore((s) => s.phase);
  const gen = useContextStore((s) => s.gen);
  const announcement = useContextStore((s) => s.announcement);
  const companyName = useContextStore((s) => s.companyName);
  const destinationCheck = useContextStore((s) => s.destinationCheck);
  const verified = !!tenant; // the company's settings are known for this context; nothing module-bound is decided before
  /** Why a destination is not active: the company switched it off, this release does not ship it, or the company is not loaded yet. */
  const inactive = (it: Item): 'disabled' | 'unavailable' | 'loading' | null =>
    !it.module
      ? null
      : !verified
        ? 'loading'
        : !workspace.available[it.module]
          ? 'unavailable'
          : !workspace.enabled[it.module]
            ? 'disabled'
            : null;
  const busy = phase.kind !== 'idle' && phase.kind !== 'failed';
  const shownName = tenant?.name ?? companyName ?? '…';

  /**
   * The company refusing this session ends it, whether the refusal comes at boot or on a
   * re-read of a company that was verified a minute ago. An expired JWT answers 403 rather
   * than 401, so the request layer does not end the session itself; and clearing the cached
   * answers is not a refusal screen on its own, because the stale company answer would go on
   * supplying the navigation and the capabilities while the page stayed mounted
   * (Codex decision, 2026-09-16).
   *
   * Three things are deliberately not this: a refusal during a switch, which is access-lost
   * and belongs to shell/switch.ts; a transport failure, which refused nothing; and a 403
   * from some other route, which is one screen being told no, not the session ending.
   */
  useEffect(() => {
    if (phase.kind !== 'idle' || destinationCheck || tenantStatus !== 'error') return;
    if (tenantError instanceof ApiError && (tenantError.status === 401 || tenantError.status === 403))
      logout();
  }, [phase.kind, destinationCheck, tenantStatus, tenantError]);

  // the new company's settings could not be loaded: the company stays, its data does not appear
  useEffect(() => {
    if (destinationCheck && phase.kind === 'switching' && tenantStatus === 'error')
      setPhase({ kind: 'load-failed', company: destinationCheck.name });
  }, [destinationCheck, phase.kind, tenantStatus]);

  // after a committed switch, once the new company is verified: keep the route only if it is permitted here
  useEffect(() => {
    if (!destinationCheck || !tenant || tenant.id !== destinationCheck.id) return;
    const { name, fromPath } = destinationCheck;
    setDestinationCheck(null);
    // the new company has answered by now, so this is that company's own permissions rather
    // than a guess from the role name it was switched with (custom roles, 2026-09-16)
    const caps = capsFrom(me?.permissions, me?.is_admin === true);
    const items = ITEMS.filter((it) => lit(it, fromPath));
    // this effect owns its module check, so the company it reads is an explicit dependency
    const active = (it: Item) =>
      !it.module || (workspace.available[it.module] && workspace.enabled[it.module]);
    const allowed = (it: Item) =>
      (!it.cap || caps.includes(it.cap)) && !(it.unless && caps.includes(it.unless)) && active(it);
    const permitted = items.length ? items.some(allowed) : fromPath === '/min' || fromPath === '/';
    if (!permitted) {
      const home = ITEMS.find(allowed);
      nav(home?.to ?? '/min', { replace: true });
      announce(
        T('Byttet til {company}. Tilgangen din er annerledes her.', {
          company: name,
        }),
      );
    } else announce(T('Byttet til {company}.', { company: name }));
    setPhase({ kind: 'idle' });
    setTimeout(() => {
      const h = document.querySelector('#main h1') as HTMLElement | null;
      if (h) {
        h.tabIndex = -1;
        h.focus();
      }
    }, 50);
  }, [destinationCheck, tenant, nav, workspace, me]);

  const companyItems = tenants.map((t) => ({
    key: String(t.id),
    label: `${t.id === tenant?.id ? '✓ ' : ''}${t.name}`,
    onSelect: () => switchTo(t.id),
    disabled: busy,
  }));
  const showContent =
    verified &&
    (phase.kind === 'idle' ||
      phase.kind === 'failed' ||
      phase.kind === 'confirm' ||
      phase.kind === 'finishing' ||
      phase.kind === 'unknown-write');
  const initials = (n: string) =>
    n
      .split(' ')
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

  // the phone bar and Mer menu carry every active destination the person may see; both optional workflows can be there at once
  // a company that failed to load has no known role: no menu rather than the wrong one (#272 B-45)
  const lost = !verified && tenantStatus === 'error';
  const shows = (it: Item) => !lost && (!it.cap || can(it.cap)) && !(it.unless && can(it.unless));
  const adminShows = ITEMS.some((it) => it.group === 'admin' && shows(it));
  const visible = ITEMS.filter((it) => shows(it) && inactive(it) === null && !(adminShows && it.alone));
  // the projects entry carries the company's own word (Prosjekter, Oppdrag, Objekter; #115)
  const labelOf = (it: Item) =>
    it.module === 'projects'
      ? projectWord.many
      : it.to === '/vaktplan' && mode !== 'rota'
        ? T('Uka')
        : T(adminShows && it.besideAdmin ? it.besideAdmin : it.label);
  // the screens search can find: exactly the menu this person has (#50)
  const searchPages = visible.map((it) => ({ to: it.to, label: labelOf(it) }));

  const companyMenu = (
    <Menu
      trigger={
        <button type="button" className="ten" disabled={busy} aria-busy={busy} title={shownName}>
          {/* the company's name alone: the mode is in the menu itself (Vaktplan or Uka), not a label here (#272 B-51) */}
          <span className="tenn">{shownName}</span>
          {tenants.length > 1 && <span aria-hidden="true"> ▾</span>}
        </button>
      }
      items={companyItems}
    />
  );
  const userMenu = (
    <Menu
      trigger={
        <button
          type="button"
          className="me"
          title={properName(user?.name)}
          aria-label={properName(user?.name) || T('Meg')}
        >
          <span className={`av ${avTone(user?.id ?? user?.name)}`}>{initials(user?.name || '?')}</span>
          <span className="nm">{properName(user?.name)}</span>
        </button>
      }
      items={[
        // the settings route is guarded, so offering it without the capability only bounced the
        // person back to Min side with nothing said
        ...(can('innstillinger.se')
          ? [
              {
                key: 'settings',
                label: T('Innstillinger'),
                onSelect: () => nav('/innstillinger/arbeidsmate'),
              },
            ]
          : []),
        ...(tenant?.me?.is_admin && !tenant.is_demo && tenant.billing?.enabled
          ? [
              {
                key: 'billing',
                label: T('Abonnement'),
                onSelect: () => nav('/abonnement'),
              },
            ]
          : []),
        {
          key: 'profile',
          label: T('Profil'),
          onSelect: () => nav('/min/profil'),
        },
        ...themeItems,
        {
          key: 'logout',
          label: T('Logg ut'),
          onSelect: () => clear(),
          danger: true,
        },
      ]}
    />
  );
  // a workflow the company has switched off, or this release lacks, is not offered at all (design
  // guidelines 2026-10-08: optional does not mean dimmed items scattered over the menu); while the
  // company is loading it shows dimmed, so the menu does not jump
  // the phone bar holds the first four; a page that is none of them is behind Mer (#274 N02)
  const behindMer = !visible.slice(0, 4).some((it) => lit(it, pathname, search));
  // the item drawn as selected is also the one announced as the current page, on a sub-route too (/arbeidstid
  // under Kontroll, UI consistency v1 CP-01); a NavLink announces only its own path, so lit() decides both
  const navLink = (it: Item, why: ReturnType<typeof inactive>) => (
    <Link
      key={`${it.to}-${it.label}`}
      to={it.to}
      aria-current={lit(it, pathname, search) ? 'page' : undefined}
      className={`${lit(it, pathname, search) ? 'on' : ''}${why ? ' dim' : ''}`}
      title={
        why === 'loading' ? T('Laster bedriftens innstillinger…') : sideCollapsed ? labelOf(it) : undefined
      }
    >
      <Ic name={it.icon} />
      <span className="txt">{labelOf(it)}</span>
    </Link>
  );

  return (
    <>
      <SupportBanner />
      {/* one top bar on every width since the manager redesign (2026-10-09): the brand, the company,
          then help, search, the person's own notifications and the account */}
      {/* the first stop for a keyboard: past the bar and the menu to the page (#272 B-50) */}
      <a className="skip" href="#main">
        {T('Hopp til innhold')}
      </a>
      <header id="top">
        {/* the index route sends each role to the screen it may see; /innboks (now /varsler) bounced an employee */}
        <NavLink className="brand" to="/" aria-label={T('Timefly, til forsiden')}>
          {T('timefly')}
        </NavLink>
        {companyMenu}
        {tenant?.is_demo && <span className="tdemo">{T('Demobedrift')}</span>}
        <span className="sp" />
        <TrialChip />
        {/* the global search as a field in the top bar, with its own key (owner 18.10); the phone keeps the magnifier */}
        <SearchButton top />
        {/* Hugin opens from the top bar, so no corner button covers a page's last action (#272) */}
        <button
          type="button"
          className="bell tophugin"
          aria-label={T('Spør {name}', { name: 'Hugin' })}
          title={T('Spør {name}', { name: 'Hugin' })}
          onClick={() => window.dispatchEvent(new Event('timefly:ai-open'))}
        >
          <HuginMark size={22} />
        </button>
        <HelpButton />
        <SearchButton compact />
        <Bell />
        {userMenu}
      </header>
      <output className="sr" aria-live="polite">
        {announcement ?? ''}
      </output>
      <GlobalSearch pages={searchPages} />
      {/* one leave question on every page, Vaktplan included (issue #72); its route half needs the data router */}
      <LeaveGuard />
      <Welcome />
      <nav id="side" className={sideCollapsed ? 'collapsed' : undefined} aria-label={T('Hovedmeny')}>
        <button
          type="button"
          className="side-toggle"
          onClick={toggleSidebar}
          aria-label={sideCollapsed ? T('Utvid sidemenyen') : T('Skjul menytekst')}
          title={sideCollapsed ? T('Utvid sidemenyen') : T('Skjul menytekst')}
          aria-expanded={!sideCollapsed}
        >
          <Icon name="panelLeft" />
          <span className="txt">{T('Skjul menytekst')}</span>
        </button>
        {GROUPS.map((g) => {
          const mine = ITEMS.filter(
            (it) =>
              it.group === g.key &&
              shows(it) &&
              !(adminShows && it.alone) &&
              inactive(it) !== 'disabled' &&
              inactive(it) !== 'unavailable',
          );
          if (!mine.length) return null;
          // the personal pages fold up below a manager's own; an employee sees only theirs, unheaded
          const folds = adminShows && g.key === 'self';
          const open = !folds || selfOpen || mine.some((it) => lit(it, pathname, search));
          return (
            <div key={g.key} className={`sgroup sg-${g.key}`}>
              {folds && (
                <button type="button" className="sgh sfold" aria-expanded={open} onClick={toggleSelf}>
                  <span className="txt">{T(g.label)}</span> <span aria-hidden="true">{open ? '▾' : '▸'}</span>
                </button>
              )}
              {open && mine.map((it) => navLink(it, inactive(it)))}
            </div>
          );
        })}
      </nav>
      <main id="main" tabIndex={-1}>
        <DemoBanner />
        <SetupBanner />
        <TrialBanner />
        {phase.kind === 'switching' && (
          <output className="empty" aria-live="polite">
            {T('Bytter til {company}…', { company: phase.target.name })}
          </output>
        )}
        {phase.kind === 'session-changed' && (
          <div className="empty" role="alert">
            <p>{T('Økten ble endret i en annen fane. Last siden på nytt for å fortsette.')}</p>
            <Button onClick={() => window.location.reload()}>{T('Last på nytt')}</Button>
          </div>
        )}
        {phase.kind === 'unverified' && (
          <div className="empty" role="alert">
            <p>{T('Kunne ikke bekrefte hvilken bedrift som er aktiv. Prøv igjen.')}</p>
            <Button
              onClick={async () => {
                // the retry revalidates the context itself; actions come back only if it answers
                const r = await refetchTenant();
                if (r.status === 'success') setPhase({ kind: 'idle' });
              }}
            >
              {T('Prøv igjen')}
            </Button>
          </div>
        )}
        {phase.kind === 'access-lost' && (
          <div className="empty" role="alert">
            <p>{T('Du har ikke lenger tilgang til {company}. Logg inn på nytt.', { company: shownName })}</p>
            <Button onClick={() => clear()}>{T('Logg inn på nytt')}</Button>
          </div>
        )}
        {phase.kind === 'idle' && !verified && tenantStatus === 'pending' && (
          <output className="empty" aria-live="polite">
            {T('Laster {company}…', { company: shownName })}
          </output>
        )}
        {((phase.kind === 'idle' && !verified && tenantStatus === 'error') ||
          phase.kind === 'load-failed') && (
          <div className="empty" role="alert">
            <p>
              {/* no name yet is «bedriften», never «…» (#272 B-45) */}
              {phase.kind === 'load-failed' || tenant?.name || companyName
                ? T('Kunne ikke laste {company}.', {
                    company: phase.kind === 'load-failed' ? phase.company : shownName,
                  })
                : T('Kunne ikke laste bedriften.')}
            </p>
            <Button
              onClick={() => {
                if (phase.kind === 'load-failed' && destinationCheck)
                  setPhase({
                    kind: 'switching',
                    target: {
                      id: destinationCheck.id,
                      name: destinationCheck.name,
                    },
                  });
                void refetchTenant();
              }}
            >
              {T('Prøv igjen')}
            </Button>
          </div>
        )}
        {newVersion && (
          <output className="banner" aria-live="polite" data-testid="new-version">
            <span>{T('Timefly er oppdatert. Last inn siden for å få den nye versjonen.')}</span>
            <Button small onClick={() => window.location.reload()}>
              {T('Last inn på nytt')}
            </Button>
          </output>
        )}
        {phase.kind === 'failed' && (
          <div className="banner" role="alert">
            <span>
              {T('Kunne ikke bytte bedrift. Du er fortsatt i {company}.', {
                company: shownName,
              })}
            </span>
            <Button small ghost onClick={dismiss}>
              {T('OK')}
            </Button>
          </div>
        )}
        {phase.kind === 'finishing' && (
          <output className="banner" aria-live="polite">
            <span>{T('Fullfører endringen din før bedriften byttes.')}</span>
          </output>
        )}
        {phase.kind === 'unknown-write' && (
          <div className="banner" role="alert">
            <span>
              {T('Utfallet av den siste endringen i {company} er ukjent. Sjekk den før du bytter bedrift.', {
                company: shownName,
              })}
            </span>
            <Button small ghost onClick={() => setPhase({ kind: 'idle' })}>
              {T('Bli i {company}', { company: shownName })}
            </Button>
          </div>
        )}
        {phase.kind === 'confirm' && (
          <div className="confirm">
            <div
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="sw-title"
              onKeyDown={(e) => {
                if (e.key === 'Escape') stay();
              }}
            >
              <h3 id="sw-title">
                {T('Du har ulagrede endringer i {company}.', {
                  company: shownName,
                })}
              </h3>
              <p>
                {T('Bytter til {company}…', {
                  company: phase.target.name,
                }).replace('…', '?')}
              </p>
              <div className="df">
                <Button onClick={stay} autoFocus>
                  {T('Bli og fortsett å redigere')}
                </Button>
                <Button ghost onClick={discardAndSwitch}>
                  {T('Forkast endringene og bytt')}
                </Button>
              </div>
            </div>
          </div>
        )}
        {showContent && <Outlet key={`${locale}:${gen}`} />}
      </main>
      {/* The assistant is for roles given "Bruke AI-assistenten" (ai.use, owner 2026-09-25). The server
          refuses anyone else with a 403; this only stops offering a button that could never work. */}
      {can('assistent.bruke') && <PocChatPanel />}
      {/* phone: the sidebar is hidden at 800px and below (styles.css); the first four screens sit in a bottom bar, the rest behind Mer */}
      <nav id="pnav" aria-label={T('Meny')}>
        {visible.slice(0, 4).map((it) => (
          // the same rule as the sidebar: a tab of a destination lights it, and says so to a screen reader (#274 N02)
          <Link
            key={`${it.to}-${it.label}`}
            to={it.to}
            className={lit(it, pathname, search) ? 'on' : ''}
            aria-current={lit(it, pathname, search) ? 'page' : undefined}
          >
            <Ic name={it.icon} />
            {labelOf(it)}
          </Link>
        ))}
        <Menu
          trigger={
            // a page behind Mer lights Mer, so the bar always shows where you are (#274 N02)
            <button
              type="button"
              className={behindMer ? 'more on' : 'more'}
              aria-current={behindMer ? 'page' : undefined}
            >
              <Ic name="more" />
              {T('Mer')}
            </button>
          }
          items={[
            ...visible.slice(4).map((it) => ({
              key: it.to,
              label: (
                <>
                  <Ic name={it.icon} />
                  {labelOf(it)}
                </>
              ),
              onSelect: () => nav(it.to),
            })),
            ...tenants
              .filter((t) => t.id !== tenant?.id)
              .map((t) => ({
                key: `t${t.id}`,
                label: (
                  <>
                    <Ic name="swap" />
                    {t.name}
                  </>
                ),
                onSelect: () => switchTo(t.id),
                disabled: busy,
              })),
            ...themeItems,
            {
              key: 'help',
              label: (
                <>
                  <Ic name="help" />
                  {T('Hjelp')}
                </>
              ),
              onSelect: () => window.open(helpUrl(pathname, mode === 'rota'), '_blank', 'noreferrer'),
            },
            {
              key: 'logout',
              label: T('Logg ut'),
              onSelect: () => clear(),
              danger: true,
            },
          ]}
        />
      </nav>
      <Toast />
    </>
  );
}
